import { attrGroupLinkedTables, getColDefaultValue } from "components/PimDashboard/data";
import { inputTypes, datatypes } from "data/globalData";
import { isAttributeMandatory, validateMandatoryFields } from "utils/mandatoryFieldsLogic";

/** Colour / palette / style ref / theme may differ row-to-row for new-create rows only */
export const isBulkPerRowColorVariantKey = (attrKey) => {
	const k = String(attrKey || "")
		.toLowerCase()
		.replace(/\s+/g, "_");
	if (k === "colour" || k === "color") return true;
	if (k.includes("palette")) return true;
	if (k === "theme" || k.endsWith("_theme")) return true;
	if (k.includes("style_ref") || k.includes("styleref") || k === "reference_no") return true;
	return false;
};

export const normalizeFlatRowStrings = (row) => {
	const flat = { ...(row || {}) };
	Object.keys(flat).forEach((k) => {
		if (typeof flat[k] === "string") flat[k] = flat[k].trim();
	});
	return flat;
};

export const parseMultipleSelectValue = (raw) => {
	if (raw === null || raw === undefined || raw === "") return [];
	if (Array.isArray(raw)) return raw.map(String);
	const s = String(raw).trim();
	if (!s) return [];
	if (s.startsWith("[")) {
		try {
			const parsed = JSON.parse(s);
			return Array.isArray(parsed) ? parsed.map(String) : [];
		} catch {
			return [];
		}
	}
	return s
		.split(",")
		.map((x) => x.trim())
		.filter(Boolean);
};

const normalizeScalar = (v) => {
	if (v === null || v === undefined) return "";
	if (typeof v === "string") return v.trim();
	return v;
};

const allowedSelectValuesForRow = (attr, flatRow, allAttrs) => {
	const vals = attr.attributeValues || [];
	const dependentCols = allAttrs.filter((a) => attr.dependency?.[a.key] === true);
	if (!dependentCols.length) return vals.map((vok) => vok.value);
	return vals
		.filter((attrVal) => {
			// If this option has no dependency map at all, it is always valid
			if (!attrVal.dependency || Object.keys(attrVal.dependency).length === 0) return true;
			let ok = true;
			dependentCols.forEach((col) => {
				const parentVal = flatRow[col.key];
				const def = getColDefaultValue(col);
				const hasParent =
					parentVal !== null &&
					parentVal !== undefined &&
					parentVal !== "" &&
					!(Array.isArray(parentVal) && parentVal.length === 0) &&
					parentVal !== def;
				if (hasParent) {
					const depList = attrVal.dependency?.[col.key];
					// If this option has no restriction for this particular parent col, allow it
					if (depList && depList.length > 0 && !depList.includes(String(parentVal).trim())) ok = false;
				}
			});
			return ok;
		})
		.map((vok) => vok.value);
};

export const validateAttributeValuesAgainstMaster = (flat, allAttrs) => {
	for (const attr of allAttrs) {
		if (attr.input_type === inputTypes.AUTO_GENERATE.key) continue;
		if (!Object.prototype.hasOwnProperty.call(flat, attr.key)) continue;
		const rawVal = flat[attr.key];
		const val = normalizeScalar(rawVal);

		if (attr.input_type === inputTypes.SELECT.key) {
			if (val === "" || val === null) continue;
			const allowed = new Set(allowedSelectValuesForRow(attr, flat, allAttrs));
			if (!allowed.has(val)) {
				return `Invalid value for '${attr.name}' (${attr.key}): '${rawVal}'`;
			}
		} else if (attr.input_type === inputTypes.MULTIPLE_SELECT.key) {
			const parts = parseMultipleSelectValue(rawVal);
			const allowed = new Set(allowedSelectValuesForRow(attr, flat, allAttrs));
			for (const p of parts) {
				if (!allowed.has(p)) return `Invalid value for '${attr.name}' (${attr.key}): '${p}'`;
			}
		} else if (attr.input_type === inputTypes.ENTER.key) {
			if (val === "") continue;
			if (attr.data_type === datatypes.NUMBER.key && Number.isNaN(Number(val))) {
				return `Invalid number for '${attr.name}' (${attr.key})`;
			}
			if (attr.data_type === datatypes.DECIMAL.key && Number.isNaN(parseFloat(val))) {
				return `Invalid decimal for '${attr.name}' (${attr.key})`;
			}
		}
	}
	return null;
};

export const bulkCellProvidedAndFilled = (attr, rawVal) => {
	if (attr.input_type === inputTypes.MULTIPLE_SELECT.key) return parseMultipleSelectValue(rawVal).length > 0;
	const val = normalizeScalar(rawVal);
	if (val === "" || val === null || val === undefined) return false;
	if (
		attr.input_type === inputTypes.ENTER.key &&
		(attr.data_type === datatypes.NUMBER.key || attr.data_type === datatypes.DECIMAL.key)
	) {
		if (Number.isNaN(Number(val))) return false;
	}
	return true;
};

const assembleNestedBodiesPreview = (flat, allAttrs) => {
	const style = {};
	const style_details = {};
	const color = {};
	for (const attr of allAttrs) {
		if (attr.input_type === inputTypes.AUTO_GENERATE.key) continue;
		if (!Object.prototype.hasOwnProperty.call(flat, attr.key)) continue;
		let v = flat[attr.key];
		if (typeof v === "string") v = v.trim();
		if (attr.input_type === inputTypes.MULTIPLE_SELECT.key) v = parseMultipleSelectValue(v);
		if (v === "") continue;
		if (Array.isArray(v) && v.length === 0) continue;
		if (attr.linked_table === attrGroupLinkedTables.style.key) style[attr.key] = v;
		else if (attr.linked_table === attrGroupLinkedTables.style_details.key) style_details[attr.key] = v;
		else if (attr.linked_table === attrGroupLinkedTables.color.key) color[attr.key] = v;
	}
	return { style, style_details, color };
};

const serializeColorConsistencyValue = (attr, rawFromFlat) => {
	if (attr.input_type === inputTypes.MULTIPLE_SELECT.key)
		return JSON.stringify(parseMultipleSelectValue(rawFromFlat).slice().sort());
	const v = normalizeScalar(rawFromFlat);
	return typeof v === "number" ? String(v) : String(v);
};

export const extractColorConsistencySnapshot = (flat, allAttrs) => {
	const snapshot = {};
	for (const attr of allAttrs) {
		if (attr.linked_table !== attrGroupLinkedTables.color.key) continue;
		if (attr.input_type === inputTypes.AUTO_GENERATE.key) continue;
		if (isBulkPerRowColorVariantKey(attr.key)) continue;
		if (!Object.prototype.hasOwnProperty.call(flat, attr.key)) {
			snapshot[attr.key] = "";
			continue;
		}
		snapshot[attr.key] = serializeColorConsistencyValue(attr, flat[attr.key]);
	}
	return snapshot;
};

export const colorConsistencySnapshotsEqual = (a, b) => {
	const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
	for (const k of keys) {
		if ((a[k] ?? "") !== (b[k] ?? "")) return false;
	}
	return true;
};

const snapshotMismatchKey = (a, b) => {
	const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
	for (const k of keys) {
		if ((a[k] ?? "") !== (b[k] ?? "")) return k;
	}
	return null;
};

/** Snapshot from the earliest new-create row (empty style_id) in file order */
export const baselineColorConsistencyForNewCreates = (parsedRows, allAttrs) => {
	for (let i = 0; i < parsedRows.length; i++) {
		const f = normalizeFlatRowStrings(parsedRows[i]);
		if (f.style_id && String(f.style_id).trim()) continue;
		return extractColorConsistencySnapshot(f, allAttrs);
	}
	return null;
};

export function getBulkUploadRowStatusMessage(flatPlain, allAttrs, parsedRows = []) {
	const flat = normalizeFlatRowStrings(flatPlain);
	const typeErr = validateAttributeValuesAgainstMaster(flat, allAttrs);
	if (typeErr) return typeErr;

	const sidPresent = !!(flat.style_id && String(flat.style_id).trim());

	const { style, style_details, color } = assembleNestedBodiesPreview(flat, allAttrs);

	const nestedForMandatory = {
		[attrGroupLinkedTables.style.key]: style,
		[attrGroupLinkedTables.color.key]: color,
		[attrGroupLinkedTables.style_details.key]: style_details,
	};

	if (sidPresent) {
		return "";
	}

	if (!nestedForMandatory[attrGroupLinkedTables.style.key]?.brand) {
		return "brand is required when creating a new style";
	}

	const brand = nestedForMandatory[attrGroupLinkedTables.style.key]?.brand || "";
	const category =
		nestedForMandatory[attrGroupLinkedTables.style.key]?.department ||
		nestedForMandatory[attrGroupLinkedTables.style.key]?.category ||
		"";

	const editableStyleAttrs = allAttrs.filter(
		(a) => a.linked_table === attrGroupLinkedTables.style.key && a.input_type !== inputTypes.AUTO_GENERATE.key,
	);
	for (const attr of editableStyleAttrs) {
		if (!isAttributeMandatory(attr, brand, category)) continue;
		if (!Object.prototype.hasOwnProperty.call(flat, attr.key) || !bulkCellProvidedAndFilled(attr, flat[attr.key])) {
			return `New styles: mandatory Style field missing — '${attr.name}' (${attr.key})`;
		}
	}

	const editableColorAttrs = allAttrs.filter(
		(a) => a.linked_table === attrGroupLinkedTables.color.key && a.input_type !== inputTypes.AUTO_GENERATE.key,
	);
	for (const attr of editableColorAttrs) {
		if (!isAttributeMandatory(attr, brand, category)) continue;
		if (!Object.prototype.hasOwnProperty.call(flat, attr.key) || !bulkCellProvidedAndFilled(attr, flat[attr.key])) {
			return `New styles: mandatory Color field missing — '${attr.name}' (${attr.key})`;
		}
	}

	const detailAttrs = allAttrs.filter(
		(a) => a.linked_table === attrGroupLinkedTables.style_details.key && a.input_type !== inputTypes.AUTO_GENERATE.key,
	);
	const d = validateMandatoryFields(nestedForMandatory, detailAttrs, brand, category, attrGroupLinkedTables.style_details.key);
	if (!d.isValid) return `Missing Style Details field(s): ${d.missingFields.map((fok) => fok.name).join(", ")}`;

	const baseline = baselineColorConsistencyForNewCreates(parsedRows, allAttrs);
	if (baseline) {
		const snap = extractColorConsistencySnapshot(flat, allAttrs);
		if (!colorConsistencySnapshotsEqual(baseline, snap)) {
			const m = snapshotMismatchKey(baseline, snap);
			return m
				? `New styles: Colour, Palette, Theme, Style ref may differ per row — other Color fields must match row 1 of new uploads (difference: '${m}').`
				: `New styles: Colour, Palette, Theme, Style ref may differ — other Color fields must match row 1 of new uploads.`;
		}
	}

	return "";
}
